import React from 'react';

import { TOOL_IDS } from '@adsk/alloy-provider';
import { DocumentTextIcon } from '@adsk/alloy-react-icon';
import { useOnlyLocale } from '@adsk/alloy-react-locale';
import { TabTitle } from '@adsk/exo-skeleton-react';

import DocsActivityCard from './DocsActivityCard';
import DocsCustomDataView from './DocsCustomDataView';
import DocsCustomTopBanner from './DocsCustomTopBanner';
import EntityBlock from './DocsEntityBlock';
import EntityMenuItem from './DocsEntityMenuItem';
import DocsMissingEntities from './DocsMissingEntities';
import {
  FOLDER_LIMIT,
  DOCUMENT_LIMIT,
  SEARCH_FOLDER_LIMIT,
} from './helpers/consts';
import { formatEntityBlock, formatEntityItem } from './helpers/formatEntities';
import Services, { LocaleUtil, CANCEL_KEYS } from './helpers/sdkServices';
import { getTopFolderUrn } from './helpers/searchHelper';
import { BOOTSTRAP_NODE } from './helpers/tree';
import { chunk, getDistributedAndSortedFolders, uniq } from './helpers/utils';
import { useTranslation } from './i18n';
import UploadPanel from './UploadPanel';

const TopBarActions = () => {
  const { locale } = useOnlyLocale();

  // When initialize show the picker modal, setup locale into SDK for folder name localization
  React.useEffect(() => {
    LocaleUtil.setLocale(locale);
  }, [locale]);

  // This is to use an empty TopBarActions component to initialized SDK required `locale` and `feature flag` info since
  // there is no other way to set up them before the plugin start to load data from SDK.
  return null;
};

const PickerModalTitle = ({ showTabTitle = true }) => {
  const { t } = useTranslation();
  const value = t('ADD_DOCUMENT');

  return (
    <>
      {showTabTitle && <TabTitle value={value} />}
      {value}
    </>
  );
};

// export for testing
export const folderCache = new Map();

const createDocsPlugin = (
  defaultExpandedFolderUrn,
  enableFolderSearching = false,
  enableGetFolderPermission = false,
) => {
  /**
   * A map to save selected entities:
   * We need this to keep full entity info instead of just id.
   * It can't be placed inside DocsCustomDataView as it will be remounted after switching search mode.
   */
  const entitiesCache = new Map();
  const onGetCachedEntity = (key) => entitiesCache.get(key);
  const onRefreshEntities = (entities) => {
    return entities.map((entity) => {
      const key = entity.urn || entity.id; // Prefer urn over id
      if (!key) return entity;
      const merged = { ...entitiesCache.get(key), ...entity };
      entitiesCache.set(key, merged);
      return merged;
    });
  };

  const docsPlugin = {
    metadata: {
      domain: 'autodesk-bim360-documentmanagement',
      type: 'documentlineage',
    },
    is3LOSupported: true,
    renderIcon: DocumentTextIcon.renderIcon,
    toolId: TOOL_IDS.DOCUMENTS,
    showSearchBox: true,
    modalProps: {
      width: 800,
      height: 700,
    },
    PluralText: ({ lowerCase }) => {
      const { t } = useTranslation();
      const result = t('TITLE');
      if (lowerCase && typeof result === 'string') {
        return result.toLowerCase();
      }
      return result;
    },
    PickerModalTitle,
    TopBarActions,
    getCanCreateArgs: ({ createFormProps }) => ({
      parentFolderUrn: createFormProps?.parentFolderUrn,
    }),
    canCreate: ({ parentFolderUrn }) => Promise.resolve(!!parentFolderUrn),
    CreateNewButtonText: () => {
      const { t } = useTranslation();
      return t('UPLOAD_NEW');
    },
    CreateNewEntityPanel: UploadPanel,
    AddRelationshipsButtonText: ({ count }) => {
      const { t } = useTranslation();
      return t('ADD_BUTTON', { count });
    },
    SelectedCount: ({ count }) => {
      const { t } = useTranslation();
      return t('SELECTED_COUNT', { count });
    },
    queryEntities: async ({
      projectId,
      folderUrn,
      pageNumber = 1,
      filters = {},
    }) => {
      const isSearchMode = !!filters.searchExpression;
      if (isSearchMode) {
        let queryUrns = Array.isArray(folderUrn)
          ? folderUrn
          : folderUrn && typeof folderUrn === 'string'
            ? [folderUrn]
            : undefined;

        // re-fetch top folder urn if empty
        if (!queryUrns) queryUrns = await getTopFolderUrn(projectId);
        if (!queryUrns) return { data: [] };

        // Call search service to get the target document, and split into chunks to avoid
        // filter parameter exceed 2000 characters limit.
        const folderChunks = chunk(queryUrns, SEARCH_FOLDER_LIMIT);
        const folderResults = await Promise.all(
          folderChunks.map((folderUrns) =>
            Services.Search.proxySearch(
              projectId,
              {
                searchText: filters.searchExpression || '',
                folderUrns,
                filters: { entityType: { value: ['CONTAINER', 'SEED_FILE'] } },
                sort: [{ field: 'name', order: 'asc' }],
                pageNumber,
                recursive: true,
                includeContent: false,
                includeFolders: enableFolderSearching,
                includeAttributes: false,
                includePermission: enableGetFolderPermission,
                includeTopFolders: enableFolderSearching,
              },
              {
                [CANCEL_KEYS]: undefined, // Override the default cancel config, otherwise one request will be canceled by another
              },
            ),
          ),
        );

        // Merge and sort the results by names
        const folders = folderResults.flatMap((result) => result.folders);
        const documents = folderResults.flatMap((result) => result.documents);
        const pagination = folderResults.flatMap((result) => result.pagination);

        // Whether any batch has next page and go to next query if needed
        // Update queryUrns based on `folderChunks` and `pagination`
        queryUrns = folderChunks
          .filter((_, index) => pagination[index].totalPages > pageNumber)
          .flatMap((chunk) => chunk);
        const hasNext = queryUrns.length > 0;

        // Search has more data and previous batch are all deleted, it should auto fetch next batch
        const hasMoreData = enableFolderSearching
          ? documents.length === 0 && folders.length === 0
          : documents.length === 0;

        if (hasNext && hasMoreData) {
          return await docsPlugin.queryEntities({
            projectId,
            folderUrn: queryUrns,
            filters,
            pageNumber: pageNumber + 1,
          });
        }

        return {
          data: enableFolderSearching
            ? [
                ...getDistributedAndSortedFolders(folders).map(
                  formatEntityItem,
                ),
                ...documents.map(formatEntityItem),
              ]
            : documents.map(formatEntityItem),
          ...(hasNext && {
            nextPageQuery: {
              projectId,
              folderUrn: queryUrns,
              filters,
              pageNumber: pageNumber + 1,
            },
          }),
        };
      } else {
        return {
          data: [BOOTSTRAP_NODE],
        };
      }
    },

    getEntitiesByIds: async ({ ids, projectId }) => {
      try {
        const chunkIds = chunk(ids, DOCUMENT_LIMIT);
        const chunks = await Promise.all(
          chunkIds.map((ids) =>
            Services.Projects.getProjectDocuments(projectId, null, {
              urns: ids,
            }),
          ),
        );
        const documents = chunks
          .flat()
          .map(({ results }) => results)
          .flat();
        const failedData = chunks
          .flat()
          .map(({ errors }) => errors)
          .flat()
          .map((failedDoc) => ({ ...failedDoc, id: failedDoc.urn }));
        let folders = [];

        try {
          let toFetchUrns = [];
          const folderUrns = uniq(
            documents.map(({ parentFolderUrn }) => parentFolderUrn),
          );
          folderUrns.forEach((urn) => {
            const target = folderCache.get(urn);
            if (target) folders = [...folders, target];
            if (!target) toFetchUrns = [...toFetchUrns, urn];
          });
          const chunksUrns = chunk(toFetchUrns, FOLDER_LIMIT);
          const res = await Promise.all(
            chunksUrns.map((urns) =>
              Services.Folders.getFoldersByUrns(
                projectId,
                {},
                {
                  urns,
                },
                {
                  [CANCEL_KEYS]: undefined, // Override the default cancel config, otherwise one request will be canceled by another
                },
              ),
            ),
          );
          const fetched = res.map(({ results }) => results).flat();
          fetched.forEach((folder) => folderCache.set(folder.urn, folder));
          folders.push(...fetched);
        } catch {
          /* empty */
        }

        const data = documents.map((entity) => {
          entity = formatEntityBlock(entity);
          const parentFolder = folders.find(
            (folder) => folder.urn === entity.parentFolderUrn,
          );
          parentFolder && (entity.parentFolder = parentFolder);

          return entity;
        });
        return { data: [...data, ...failedData] };
      } catch {
        return { data: [] };
      }
    },
    EntityBlock,
    CustomDataView: (props) => (
      <DocsCustomDataView
        {...props}
        onRefreshEntities={onRefreshEntities}
        onGetCachedEntity={onGetCachedEntity}
      />
    ),
    EntityMenuItem,
    ActivityCard: DocsActivityCard,
    getDefaultExpandedFolderUrn: () => defaultExpandedFolderUrn,
    CustomTopBanner: DocsCustomTopBanner,
    alwaysShowCustomMissingEntities: true,
    CustomMissingEntities: ({ loading, error, data }) => (
      <DocsMissingEntities loading={loading} error={error} data={data} />
    ),
  };

  return docsPlugin;
};

export const createPlugin = ({
  onEntityBlockClick, // a customized click handler for clicking on EntityBlock, optional, shape (entity) => *
  defaultExpandedFolderUrn,
  enableFolderSearching,
  enableGetFolderPermission,
} = {}) => {
  const docsPlugin = createDocsPlugin(
    defaultExpandedFolderUrn,
    enableFolderSearching,
    enableGetFolderPermission,
  );

  return {
    ...docsPlugin,
    PickerModalTitle: (props) => <PickerModalTitle {...props} />,
    EntityBlock: (props) => (
      <EntityBlock {...props} onClick={onEntityBlockClick} />
    ),
  };
};

export default createDocsPlugin();
